<script setup>
import { ref, reactive, onMounted } from 'vue'
import api from '@/services/api'
import { usePaginacao } from '@/composables/usePaginacao'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import { formatarDataBR } from '@/utils/useDataBR'

const emit = defineEmits(['adicionar-item', 'editar-item', 'show-notification'])

const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const colunas = [
  { field: 'idDocumentacao', label: 'ID', width: 270, sortable: true },
  { field: 'modulo', label: 'Módulo', width: 160, sortable: true },
  { field: 'tela', label: 'Tela', width: 270, sortable: true },
  { field: 'titulo', label: 'Título', sortable: true },
  { field: 'statusAtualizacao', label: 'Status', width: 100, align: 'center', slotName: 'statusAtualizacao', sortable: false },
  { field: 'versao', label: 'Versão', width: 80, align: 'center', slotName: 'versao' },
  { field: 'tsCriacao', label: 'Criado em', width: 110, sortable: true, slotName: 'tsCriacao' },
  { field: 'acoes', label: 'Ações', width: 100, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const handleSortChange = () => {
  buscarDados(1)
}

const filtros = reactive({
  modulo: '',
  tela: '',
  search: '',
  status: ''
})

const buscarDados = async (pageNumber = 1) => {
  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)
    if (filtros.modulo) params.append('modulo', filtros.modulo)
    if (filtros.tela) params.append('tela', filtros.tela)
    if (filtros.search) params.append('search', filtros.search)
    if (filtros.status) params.append('status', filtros.status)

    const response = await api.get(`/DocumentacaoTela?${params.toString()}`)
    lista.value = response.data

    atualizarPaginacao(response.headers)
  } catch (error) {
    console.error('Erro ao buscar documentações:', error)
    erro.value = 'Não foi possível carregar as documentações.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => { buscarDados(1) }

const verificando = ref(false)

const verificarAtualizacoes = async () => {
  verificando.value = true
  try {
    const response = await api.post('/DocumentacaoTela/atualizar-status-componentes')
    emit('show-notification', {
      message: `Verificação concluída: ${response.data.atualizados} atualizados, ${response.data.desatualizados} desatualizados, ${response.data.semComponente} sem componente.`,
      type: 'success'
    })
    buscarDados(1)
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao verificar atualizações.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    verificando.value = false
  }
}

const limparFiltros = () => {
  filtros.modulo = ''
  filtros.tela = ''
  filtros.search = ''
  filtros.status = ''
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
    buscarDados(page)
  }
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const adicionarNovo = () => { emit('adicionar-item') }

const editarRegistro = (item) => { emit('editar-item', item) }

const excluirRegistro = async (item) => {
  if (confirm(`Tem certeza que deseja excluir a documentação "${item.idDocumentacao}"?`)) {
    try {
      await api.delete(`/DocumentacaoTela/${item.idDocumentacaoTela}`)
      buscarDados(paginacao.value.CurrentPage)
    } catch (err) {
      alert(err.response?.data?.message || 'Erro ao excluir documentação.')
    }
  }
}

onMounted(async () => {
  buscarDados(1)
})

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Documentação de Telas"
    subtitulo="Gerencie as documentações para o assistente Tira Dúvidas"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    mostrar-botao-adicionar
    label-botao-adicionar="Nova Documentação"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="adicionarNovo"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-outline-warning me-2" @click="verificarAtualizacoes"
              :disabled="verificando">
        <i class="bi bi-arrow-repeat me-2" :class="{ 'spin': verificando }"></i>
        {{ verificando ? 'Verificando...' : 'Verificar Atualizações' }}
      </button>
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-3">
          <label class="form-label">Módulo</label>
          <input type="text" class="form-control" v-model="filtros.modulo" placeholder="Filtrar por módulo..." @keyup.enter="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <label class="form-label">Tela</label>
          <input type="text" class="form-control" v-model="filtros.tela" placeholder="Filtrar por tela..." @keyup.enter="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <label class="form-label">Busca geral</label>
          <input type="text" class="form-control" v-model="filtros.search" placeholder="ID, título ou módulo..." @keyup.enter="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <label class="form-label">Status</label>
          <select class="form-select" v-model="filtros.status" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option value="atualizado">✅ Atualizado</option>
            <option value="desatualizado">🟡 Desatualizado</option>
            <option value="sem_doc">🔴 Sem verificação</option>
          </select>
        </div>
        <div class="col-12 text-end">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhuma documentação encontrada."
      row-key="idDocumentacaoTela"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      @sort-change="handleSortChange"
    >
      <template #statusAtualizacao="{ row }">
        <span v-if="row.statusAtualizacao === 'atualizado'"
              class="badge bg-success" title="Documentação atualizada">
          <i class="bi bi-check-circle-fill me-1"></i> OK
        </span>
        <span v-else-if="row.statusAtualizacao === 'desatualizado'"
              class="badge bg-warning text-dark" title="Componente modificado após a documentação">
          <i class="bi bi-exclamation-triangle-fill me-1"></i> Desat.
        </span>
        <span v-else-if="row.statusAtualizacao === 'sem_componente'"
              class="badge bg-secondary" title="Arquivo .vue não encontrado">
          <i class="bi bi-question-circle-fill me-1"></i> N/A
        </span>
        <span v-else class="badge bg-danger" title="Nunca verificado">
          <i class="bi bi-x-circle-fill me-1"></i> Pend.
        </span>
      </template>

      <template #versao="{ row }">
        <span class="badge badge-ghost-info">v{{ row.versao }}</span>
      </template>

      <template #tsCriacao="{ row }">
        {{ formatarDataBR(row.tsCriacao) }}
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper">
          <button class="btn-icon" @click="editarRegistro(row)" title="Editar">
            <i class="bi bi-pencil-fill text-primary"></i>
          </button>
          <button class="btn-icon" @click="excluirRegistro(row)" title="Excluir">
            <i class="bi bi-trash-fill text-danger"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>
</template>

<style scoped>
.acoes-wrapper {
  opacity: 0;
  transition: opacity 0.2s ease-out;
}

tr:hover .acoes-wrapper {
  opacity: 1;
}

.btn-icon {
  background: none;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 1rem;
  border-radius: 4px;
  transition: background-color 0.15s ease;
}

.btn-icon:hover {
  background-color: var(--bs-tertiary-bg);
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.spin {
  animation: spin 1s linear infinite;
  display: inline-block;
}
</style>
